/**
 * GrowMyRestaurant — 🚀 marketing campaigns page.
 *
 * Two-branch flow (Yes / No on "Do you already have an idea?"):
 *   - YES → free-text description + file attachments (Cloudinary upload)
 *   - NO  → 4-question guided questionnaire
 *
 * After submit, shows the confirmation card and the Campaign History list.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Rocket,
  Sparkles,
  Upload,
  CheckCircle2,
  Loader2,
  Image as ImageIcon,
  X,
  Clock,
  FileText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'nofee_token';

const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CUSTOMER_TYPES = ['Families', 'Young Adults', 'Professionals', 'Tourists', 'Students', 'Seniors', 'Other'];
const AGE_RANGES = ['Under 18', '18–25', '26–40', '41–60', '60+'];
const GOALS = [
  'Attract new customers',
  'Increase repeat customers',
  'Promote a new menu',
  'Promote a new dish',
  'Increase pickup orders',
  'Increase direct online orders',
  'Other',
];

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    received:    { label: 'Received',    cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
    in_progress: { label: 'In Progress', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
    delivered:   { label: 'Delivered',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    cancelled:   { label: 'Cancelled',   cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  };
  const s = map[status] || map.received;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wider border ${s.cls}`}>
      {s.label}
    </span>
  );
}

function AttachmentTile({ url, onRemove }) {
  return (
    <div className="relative group rounded-xl border border-white/15 overflow-hidden bg-black/40" data-testid="grow-attachment">
      <img src={url} alt="Attachment" className="w-full h-24 object-cover" />
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Remove attachment"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────
export default function GrowMyRestaurant() {
  const navigate = useNavigate();

  // Form state
  const [brief, setBrief] = useState('');
  const [hasIdea, setHasIdea] = useState(null); // null | true | false
  const [ideaDescription, setIdeaDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Questionnaire
  const [typicalCustomers, setTypicalCustomers] = useState([]);
  const [cuisine, setCuisine] = useState('');
  const [typicalAge, setTypicalAge] = useState('');
  const [mainGoal, setMainGoal] = useState('');

  // Page state
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // submitted campaign payload
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    void loadHistory();
  }, []);

  async function loadHistory() {
    try {
      const [hRes, sRes] = await Promise.all([
        axios.get(`${API}/marketing/campaigns`, { headers: authHeaders() }),
        axios.get(`${API}/marketing/status`,    { headers: authHeaders() }),
      ]);
      setHistory(hRes.data?.campaigns || []);
      setStatus(sRes.data || null);
    } catch (e) {
      // Silent: history is non-critical
    } finally {
      setHistoryLoaded(true);
    }
  }

  function toggleCustomerType(t) {
    setTypicalCustomers((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  }

  async function handleFile(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (!f.type.startsWith('image/')) continue;
        if (f.size > 8 * 1024 * 1024) {
          toast.error(`${f.name} is too large (max 8 MB)`);
          continue;
        }
        // Convert to data URI and POST to /uploads/image
        const dataUri = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(f);
        });
        const { data } = await axios.post(
          `${API}/uploads/image`,
          { data_uri: dataUri, folder: 'marketing' },
          { headers: authHeaders() },
        );
        if (data?.url) setAttachments((prev) => [...prev, data.url]);
      }
    } catch (err) {
      toast.error('Upload failed — please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  function validate() {
    if (brief.trim().length < 4) {
      toast.error('Please tell us a little about your campaign.');
      return false;
    }
    if (hasIdea === null) {
      toast.error('Please answer whether you already have an idea.');
      return false;
    }
    if (hasIdea === true && ideaDescription.trim().length < 4) {
      toast.error('Please describe your idea.');
      return false;
    }
    if (hasIdea === false) {
      if (!typicalCustomers.length) { toast.error('Pick at least one customer type.'); return false; }
      if (!cuisine.trim())          { toast.error('Tell us your cuisine.'); return false; }
      if (!typicalAge)              { toast.error('Pick a typical age range.'); return false; }
      if (!mainGoal)                { toast.error('Pick your main goal.'); return false; }
    }
    return true;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const body = {
        campaign_brief: brief.trim(),
        has_idea: hasIdea,
      };
      if (hasIdea) {
        body.idea = {
          description: ideaDescription.trim(),
          attachment_urls: attachments,
        };
      } else {
        body.questionnaire = {
          typical_customers: typicalCustomers,
          cuisine: cuisine.trim(),
          typical_age: typicalAge,
          main_goal: mainGoal,
        };
      }
      const { data } = await axios.post(`${API}/marketing/campaigns`, body, { headers: authHeaders() });
      setConfirmed(data?.campaign || null);
      // Refresh history below the confirmation card
      void loadHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not submit your request.');
    } finally {
      setSubmitting(false);
    }
  }

  const formDisabled = submitting || (status && status.can_submit === false);
  const waitingForQuota = useMemo(() => {
    if (!status?.next_eligible_at) return null;
    try {
      const d = new Date(status.next_eligible_at);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  }, [status]);

  // ── Confirmation view ──
  if (confirmed) {
    return (
      <PageShell>
        <div className="rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-black p-8 sm:p-10 text-center" data-testid="grow-confirmation">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 items-center justify-center mb-5">
            <CheckCircle2 className="w-8 h-8 text-emerald-300" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">Thank you!</h2>
          <p className="text-sm text-white/70 max-w-md mx-auto leading-relaxed">
            Our marketing team has received your request. Your campaign will be delivered within <span className="text-white font-semibold">72 hours</span>.
          </p>

          <div className="mt-7 text-left max-w-md mx-auto rounded-2xl border border-white/[0.08] bg-black/30 p-5">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-bold mb-3">Depending on your objectives, your campaign may include:</p>
            <ul className="space-y-1.5 text-sm text-white/80">
              {[
                'Personalized Landing Page',
                'Facebook Post',
                'Instagram Post',
                'Instagram Story',
                'WhatsApp Promotion',
                'QR Code Materials',
                'Promotional Graphics',
              ].map((it) => (
                <li key={it} className="flex items-start gap-2">
                  <span className="text-gold-300 mt-0.5">•</span>{it}
                </li>
              ))}
            </ul>
            <p className="text-[12px] text-white/55 mt-4 leading-relaxed">
              Our marketing team will choose the best combination based on your campaign objectives.
            </p>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-black font-bold text-sm hover:bg-white/90"
              data-testid="grow-back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>

        <HistorySection history={history} loaded={historyLoaded} className="mt-10" />
      </PageShell>
    );
  }

  // ── Main form ──
  return (
    <PageShell>
      {/* Header */}
      <div className="mb-8 sm:mb-10" data-testid="grow-header">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
          Let's Grow Your Restaurant Together
        </h1>
        <p className="mt-3 text-sm sm:text-[15px] text-white/70 leading-relaxed max-w-2xl">
          Our marketing team will create customized promotional campaigns designed specifically for your restaurant and your customers. Our objective is to help increase your direct orders while strengthening your restaurant's brand.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-[12.5px] text-gold-300 font-semibold">
          <Clock className="w-4 h-4" /> Campaign delivery: up to 72 hours.
        </div>
      </div>

      {/* Quota notice */}
      {status && !status.can_submit && (
        <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="grow-quota-notice">
          You've already submitted a campaign this week. Your NoFee plan includes one personalized campaign per week — next eligible {waitingForQuota || 'in a few days'}.
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Campaign brief */}
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-6">
          <label className="block text-sm font-bold text-white mb-1">Tell us about your next campaign.</label>
          <p className="text-[12px] text-white/55 leading-relaxed mb-3">
            Examples: Promote a new menu · Lunch specials · Family meals · New dish · Grand Opening · Holiday promotion · Weekend special · Any custom promotion
          </p>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={5}
            disabled={formDisabled}
            placeholder="Describe what you want to promote and what you're hoping to achieve…"
            className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-gold-400 transition-colors resize-none"
            data-testid="grow-brief"
          />
        </div>

        {/* Has idea? */}
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-6" data-testid="grow-has-idea-block">
          <p className="text-sm font-bold text-white mb-3">Do you already have an idea for your campaign?</p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setHasIdea(true)}
              disabled={formDisabled}
              data-testid="grow-idea-yes"
              className={`px-5 py-2.5 rounded-xl text-sm font-bold border transition-colors ${hasIdea === true ? 'bg-gold-500 border-gold-500 text-black' : 'bg-black/40 border-white/15 text-white hover:border-white/30'}`}
            >
              ○ Yes
            </button>
            <button
              type="button"
              onClick={() => setHasIdea(false)}
              disabled={formDisabled}
              data-testid="grow-idea-no"
              className={`px-5 py-2.5 rounded-xl text-sm font-bold border transition-colors ${hasIdea === false ? 'bg-gold-500 border-gold-500 text-black' : 'bg-black/40 border-white/15 text-white hover:border-white/30'}`}
            >
              ○ No
            </button>
          </div>
        </div>

        {/* YES branch */}
        {hasIdea === true && (
          <div className="rounded-2xl border border-gold-500/30 bg-gold-500/[0.04] p-5 sm:p-6 space-y-4" data-testid="grow-yes-branch">
            <div>
              <label className="block text-sm font-bold text-white mb-2">Tell us about your idea.</label>
              <textarea
                value={ideaDescription}
                onChange={(e) => setIdeaDescription(e.target.value)}
                rows={5}
                disabled={formDisabled}
                placeholder="Share your concept, references, target audience, key messages…"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-gold-400 transition-colors resize-none"
                data-testid="grow-idea-desc"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-white mb-2">Upload references (optional)</label>
              <p className="text-[11.5px] text-white/55 leading-relaxed mb-2">
                Previous advertisements · Images · Artwork · Flyers · Inspiration
              </p>

              {/* Attachments preview */}
              {attachments.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                  {attachments.map((url, i) => (
                    <AttachmentTile
                      key={url}
                      url={url}
                      onRemove={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                    />
                  ))}
                </div>
              )}

              <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/25 text-white text-sm cursor-pointer hover:border-white/40 transition-colors" data-testid="grow-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Uploading…' : 'Choose files'}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFile}
                  disabled={uploading || formDisabled}
                  className="hidden"
                  data-testid="grow-upload-input"
                />
              </label>

              <p className="text-[11px] text-white/45 mt-3 leading-relaxed">
                <Sparkles className="w-3 h-3 inline mr-1 text-gold-300" />
                The more information you provide, the more personalized your campaign will be.
              </p>
            </div>
          </div>
        )}

        {/* NO branch — questionnaire */}
        {hasIdea === false && (
          <div className="rounded-2xl border border-gold-500/30 bg-gold-500/[0.04] p-5 sm:p-6 space-y-6" data-testid="grow-no-branch">
            {/* Q1 */}
            <div>
              <label className="block text-sm font-bold text-white mb-3">1. Who are your typical customers?</label>
              <div className="flex flex-wrap gap-2">
                {CUSTOMER_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleCustomerType(t)}
                    className={`px-3.5 py-2 rounded-full text-[12.5px] font-semibold border transition-colors ${typicalCustomers.includes(t) ? 'bg-gold-500 border-gold-500 text-black' : 'bg-black/40 border-white/15 text-white hover:border-white/30'}`}
                    data-testid={`grow-q1-${t.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Q2 */}
            <div>
              <label className="block text-sm font-bold text-white mb-2">2. What type of cuisine do you serve?</label>
              <input
                type="text"
                value={cuisine}
                onChange={(e) => setCuisine(e.target.value)}
                placeholder="Italian, Greek, Brazilian, Japanese, Mexican, American…"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-gold-400 transition-colors"
                data-testid="grow-q2-cuisine"
              />
            </div>

            {/* Q3 */}
            <div>
              <label className="block text-sm font-bold text-white mb-3">3. Typical customer age</label>
              <div className="flex flex-wrap gap-2">
                {AGE_RANGES.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setTypicalAge(a)}
                    className={`px-3.5 py-2 rounded-full text-[12.5px] font-semibold border transition-colors ${typicalAge === a ? 'bg-gold-500 border-gold-500 text-black' : 'bg-black/40 border-white/15 text-white hover:border-white/30'}`}
                    data-testid={`grow-q3-${a.replace(/[^0-9a-z]/gi, '-').toLowerCase()}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            {/* Q4 */}
            <div>
              <label className="block text-sm font-bold text-white mb-3">4. What is your main goal?</label>
              <div className="flex flex-wrap gap-2">
                {GOALS.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setMainGoal(g)}
                    className={`px-3.5 py-2 rounded-full text-[12.5px] font-semibold border transition-colors ${mainGoal === g ? 'bg-gold-500 border-gold-500 text-black' : 'bg-black/40 border-white/15 text-white hover:border-white/30'}`}
                    data-testid={`grow-q4-${g.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={formDisabled || hasIdea === null}
            data-testid="grow-submit"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-gold-500 text-black font-extrabold text-sm hover:bg-gold-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            {submitting ? 'Submitting…' : 'Submit Campaign Request'}
          </button>
        </div>
      </form>

      {/* Rules micro-print */}
      <div className="mt-8 rounded-2xl border border-white/[0.06] bg-black/30 p-4 text-[12px] text-white/55 leading-relaxed">
        <strong className="text-white/80">Included with your NoFee subscription:</strong>{' '}
        One personalized marketing campaign per week · Delivery within 72 hours · Up to two revision requests. Additional campaigns may be available under future premium plans.
      </div>

      <HistorySection history={history} loaded={historyLoaded} className="mt-10" />
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout shell
// ─────────────────────────────────────────────────────────────────────
function PageShell({ children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="border-b border-white/[0.06] bg-black/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold"
            data-testid="grow-nav-back"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="inline-flex items-center gap-2">
            <span className="text-lg" role="img" aria-label="rocket">🚀</span>
            <h1 className="text-[15px] font-extrabold text-white tracking-tight">Grow My Restaurant</h1>
          </div>
          <span className="w-16" />
        </div>
      </nav>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">{children}</main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Campaign history list
// ─────────────────────────────────────────────────────────────────────
function HistorySection({ history, loaded, className = '' }) {
  if (!loaded) {
    return (
      <section className={className}>
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-white/55" />
          <h2 className="text-lg font-extrabold text-white tracking-tight">Campaign History</h2>
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-white/40" />
        </div>
      </section>
    );
  }
  if (!history.length) return null;
  return (
    <section className={className} data-testid="grow-history">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-4 h-4 text-white/55" />
        <h2 className="text-lg font-extrabold text-white tracking-tight">Campaign History</h2>
      </div>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden divide-y divide-white/[0.06]">
        {history.map((c) => (
          <div key={c.id} className="px-5 py-4 flex items-center justify-between gap-3" data-testid={`grow-history-${c.sequence_no}`}>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white">Campaign #{c.sequence_no}</p>
              <p className="text-[11.5px] text-white/55 truncate mt-0.5">
                {(c.campaign_brief || '').slice(0, 90)}{(c.campaign_brief || '').length > 90 ? '…' : ''}
              </p>
            </div>
            <StatusBadge status={c.status} />
          </div>
        ))}
      </div>
    </section>
  );
}
